/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetContextUsagePathParameters,
  TLContextUsageReport,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Context Usage
 */
export const getContextUsage = (
  { conversationId }: GetContextUsagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLContextUsageReport>(
    { url: `/conversations/${conversationId}/context-usage`, method: 'GET', signal },
    options,
  );
};

export const getGetContextUsageQueryKey = ({ conversationId }: GetContextUsagePathParameters) => {
  return ['conversations', conversationId, 'context-usage'] as const;
};

export const getGetContextUsageQueryOptions = <
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetContextUsagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetContextUsageQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getContextUsage>>> = ({ signal }) =>
    getContextUsage({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetContextUsageQueryResult = NonNullable<Awaited<ReturnType<typeof getContextUsage>>>;
export type GetContextUsageQueryError = ErrorType<TLHTTPValidationError>;

export function useGetContextUsage<
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetContextUsagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getContextUsage>>,
          TError,
          Awaited<ReturnType<typeof getContextUsage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetContextUsage<
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetContextUsagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getContextUsage>>,
          TError,
          Awaited<ReturnType<typeof getContextUsage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetContextUsage<
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetContextUsagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Context Usage
 */

export function useGetContextUsage<
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetContextUsagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetContextUsageQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Context Usage
 */
export const prefetchGetContextUsageQuery = async <
  TData = Awaited<ReturnType<typeof getContextUsage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetContextUsagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getContextUsage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetContextUsageQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Context Usage
 */
export const invalidateGetContextUsage = async (
  queryClient: QueryClient,
  { conversationId }: GetContextUsagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetContextUsageQueryKey({ conversationId }) }, options);

  return queryClient;
};
